import { useState } from 'react'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import { usePlanActivationStore } from '@/store/planActivationStore'
import classNames from '@/utils/classNames'
import useLocale from '@/utils/hooks/useLocale'
import CompanyPlanConfirm from './CompanyPlanConfirm'
import EnterpriseContact from './EnterpriseContact'
import IntervalToggle from './IntervalToggle'
import PlanLimits from './PlanLimits'
import { choosePlan } from '../utils/choosePlan'
import {
    costsMoney,
    formatMoney,
    groupPlans,
    hasBothIntervals,
    isPerSeat,
    monthlyCents,
    planFor,
} from '../utils/plans'
import { useTranslation } from 'react-i18next'
import type { ChoosePlanOutcome } from '../utils/choosePlan'
import type { PlanInterval } from '../utils/plans'
import type { BillingProvider, PlanOption } from '@/@types/trackheros'

type Props = {
    plans: PlanOption[]
    currentPlanCode: string
    hasCard: boolean
    provider: BillingProvider['provider']
    /** A plan somebody picked elsewhere (onboarding) and still has to confirm. */
    intendedPlanCode?: string
    /**
     * Without the card and its "Plans" heading, for a dialog that has its own
     * title (the drawer's plan item).
     */
    bare?: boolean
    /**
     * After a choice went through. `choosePlan` has already refetched or
     * started waiting for the webhook — this is only for a caller that wants
     * to say so.
     */
    onChosen?: (planCode: string, outcome: ChoosePlanOutcome) => void
}

/**
 * The plan catalogue, one card per plan with a Monthly / Yearly toggle.
 *
 * Plans arrive as one row per interval (`pro-monthly`, `pro-yearly`) and are
 * paired by `tier`. Yearly is selected by default, as the price list asks,
 * unless the account is already on a monthly plan — then the toggle opens on
 * what they have.
 *
 * Prices are tax-exclusive (Paddle adds VAT or sales tax at checkout) and
 * yearly prices are shown per month with the yearly total beneath, which is
 * how the website prices them too.
 *
 * Under Stripe a paid plan needs a card first, so its button is disabled until
 * one is on file. Under Paddle choosing the plan opens Paddle's checkout.
 *
 * Every button is disabled while a paid plan is activating: the payment has
 * gone through, and a second click is how somebody pays twice.
 *
 * Leaving a paid plan for a free one asks first. It cancels the paid
 * subscription at Paddle *immediately* (`views._subscribe_paddle`), and since
 * the drawer's plan dialog this is a button on every page, not one at the
 * bottom of a page you went looking for.
 *
 * A plan marked `converts_account` (Team, for a freelancer) asks too: buying it
 * makes this a team account, one-way, billed from a seat minimum — see
 * `CompanyPlanConfirm`. Enterprise is not in the catalogue at all; it is a
 * contract, so it gets a "Talk to us" row under the cards.
 */
const PlanPicker = ({
    plans,
    currentPlanCode,
    hasCard,
    provider,
    intendedPlanCode,
    bare = false,
    onChosen,
}: Props) => {
    const { t } = useTranslation()
    const { locale, definition } = useLocale()
    const activating = usePlanActivationStore((state) => state.pending)
    const groups = groupPlans(plans)
    const current = plans.find((plan) => plan.code === currentPlanCode)
    const [interval, setPlanInterval] = useState<PlanInterval>(
        current?.interval === 'monthly' && costsMoney(current)
            ? 'monthly'
            : 'yearly',
    )
    const [pending, setPending] = useState<string | null>(null)
    const [failed, setFailed] = useState(false)
    const [downgradeTo, setDowngradeTo] = useState<PlanOption | null>(null)
    const [companyPlan, setCompanyPlan] = useState<PlanOption | null>(null)

    const choose = async (code: string) => {
        setPending(code)
        setFailed(false)
        try {
            const outcome = await choosePlan(code, locale)
            onChosen?.(code, outcome)
        } catch {
            setFailed(true)
        } finally {
            setPending(null)
        }
    }

    const request = (plan: PlanOption) => {
        if (plan.converts_account) {
            setCompanyPlan(plan)
            return
        }
        if (current && costsMoney(current) && !costsMoney(plan)) {
            setDowngradeTo(plan)
            return
        }
        void choose(plan.code)
    }

    const confirmDowngrade = () => {
        const plan = downgradeTo
        setDowngradeTo(null)
        if (plan) void choose(plan.code)
    }

    const confirmCompanyPlan = () => {
        const plan = companyPlan
        setCompanyPlan(null)
        if (plan) void choose(plan.code)
    }

    const shown = groups
        .map((group) => planFor(group, interval))
        .filter((plan): plan is PlanOption => Boolean(plan))

    if (shown.length === 0) return null

    const money = (cents: number, currency: string) =>
        formatMoney(cents, currency, definition.tag)

    const body = (
        <>
            <div
                className={classNames(
                    'mb-4 flex flex-wrap items-center gap-3',
                    bare ? 'justify-center' : 'justify-between',
                )}
            >
                {!bare && (
                    <span className="font-semibold">{t('billing.plans')}</span>
                )}
                {hasBothIntervals(groups) && (
                    <IntervalToggle
                        value={interval}
                        onChange={setPlanInterval}
                    />
                )}
            </div>
            {failed && (
                <Alert showIcon className="mb-4" type="danger">
                    {t('billing.chooseFailed')}
                </Alert>
            )}
            <div
                className={classNames(
                    'grid gap-4',
                    shown.length > 1 && 'md:grid-cols-2',
                    shown.length > 2 && 'lg:grid-cols-3',
                )}
            >
                {shown.map((plan) => {
                    const isCurrent = plan.code === currentPlanCode
                    const intended =
                        !isCurrent && plan.code === intendedPlanCode
                    const paid = costsMoney(plan)
                    const needsCard = provider === 'stripe' && paid && !hasCard
                    const perSeat = isPerSeat(plan)
                    const yearlyTotal =
                        plan.interval === 'yearly'
                            ? perSeat
                                ? (plan.seat_price_cents ?? 0)
                                : plan.amount_cents
                            : 0
                    return (
                        <div
                            key={plan.tier || plan.code}
                            className={classNames(
                                'flex flex-col rounded-lg border p-4',
                                isCurrent || intended
                                    ? 'border-primary'
                                    : 'border-gray-200 dark:border-gray-600',
                                intended && 'ring-2 ring-primary/30',
                            )}
                        >
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="font-semibold">
                                    {plan.name}
                                </span>
                                <span className="text-end text-sm text-gray-500 dark:text-gray-400">
                                    {!paid
                                        ? t('billing.price.free')
                                        : t(
                                              perSeat
                                                  ? 'billing.price.perSeatMonth'
                                                  : 'billing.price.perMonth',
                                              {
                                                  price: money(
                                                      monthlyCents(plan),
                                                      plan.currency,
                                                  ),
                                              },
                                          )}
                                </span>
                            </div>
                            {yearlyTotal > 0 && (
                                <p className="mt-1 text-end text-xs text-gray-500 dark:text-gray-400">
                                    {t(
                                        perSeat
                                            ? 'billing.price.billedYearlyPerSeat'
                                            : 'billing.price.billedYearly',
                                        {
                                            total: money(
                                                yearlyTotal,
                                                plan.currency,
                                            ),
                                        },
                                    )}
                                </p>
                            )}
                            {bare && plan.description && (
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    {plan.description}
                                </p>
                            )}

                            <PlanLimits
                                plan={plan}
                                className="mt-3 flex flex-1 flex-col gap-1 text-sm text-gray-500 dark:text-gray-400"
                            />

                            <Button
                                block
                                className="mt-4"
                                size="sm"
                                variant={isCurrent ? 'plain' : 'solid'}
                                disabled={
                                    isCurrent ||
                                    needsCard ||
                                    Boolean(pending) ||
                                    Boolean(activating)
                                }
                                loading={
                                    pending === plan.code ||
                                    activating === plan.code
                                }
                                onClick={() => request(plan)}
                            >
                                {t(
                                    isCurrent
                                        ? 'billing.currentPlan'
                                        : needsCard
                                          ? 'billing.addCardFirst'
                                          : 'billing.choose',
                                )}
                            </Button>
                        </div>
                    )
                })}
            </div>
            <EnterpriseContact className="mt-4" />
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                {t('billing.price.taxExcluded')}
            </p>
            <CompanyPlanConfirm
                plan={companyPlan}
                onCancel={() => setCompanyPlan(null)}
                onConfirm={confirmCompanyPlan}
            />
            <ConfirmDialog
                isOpen={Boolean(downgradeTo)}
                type="warning"
                title={t('billing.downgrade.title', {
                    plan: downgradeTo?.name ?? '',
                })}
                confirmText={t('billing.downgrade.confirm', {
                    plan: downgradeTo?.name ?? '',
                })}
                onClose={() => setDowngradeTo(null)}
                onRequestClose={() => setDowngradeTo(null)}
                onCancel={() => setDowngradeTo(null)}
                onConfirm={confirmDowngrade}
            >
                <p>
                    {t('billing.downgrade.body', {
                        current: current?.name ?? '',
                        plan: downgradeTo?.name ?? '',
                    })}
                </p>
            </ConfirmDialog>
        </>
    )

    // No `id` when bare: the dialog can open over the billing page, and the
    // `#plans` anchor the quota dialog links to belongs to the page's copy.
    return bare ? (
        <div>{body}</div>
    ) : (
        <Card className="mb-4" id="plans">
            {body}
        </Card>
    )
}

export default PlanPicker
